import { useState } from 'react';
import styled from 'styled-components';
import {
  FlexCenter,
  flexColumn,
  FlexColumn,
  Flex,
  Button,
  LoadingButton,
  TextField,
  PasswordField,
} from '@elementary/common-ui/components';
import { Typography, InputLabel } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as api from 'shared/services/settingsApi';
import { fontSize, fontWeight, space } from '@elementary/common-ui/style/utils';
import SettingsSection from './SettingsSection';

const Form = styled.form`
  ${flexColumn};
  gap: ${space('lg')};
  width: 100%;
`;

const FieldsContainer = styled(FlexColumn)`
  gap: ${space('hugeMinus')};
`;

const ErrorMessage = styled(FlexCenter)<{ $show: boolean }>`
  color: ${({ theme }) => theme.palette.status.error.bright};
  font-size: ${fontSize('sm')};
  visibility: ${({ $show }) => ($show ? 'visible' : 'hidden')};
  margin-top: ${space('lgPlus')};
`;

type PasswordForm = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

const UserProfile = () => {
  const { openSnackbar } = useSnackbar();
  const [editPassword, setEditPassword] = useState<boolean>(false);
  const { register, handleSubmit, reset, watch } = useForm<PasswordForm>({});

  const {
    mutate,
    isLoading: submitIsLoading,
    error,
  } = useMutation(
    ({ currentPassword, newPassword }: PasswordForm) =>
      api.changePasswordRequest(currentPassword, newPassword),
    {
      onSuccess: () =>
        openSnackbar({ message: 'Password changed successfully.' }),
    },
  );

  const onResetPasswordClick = () => {
    reset({ currentPassword: '' });
    setEditPassword(true);
  };

  const onSubmit = (formData: PasswordForm) => {
    mutate(formData);
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <SettingsSection sx={{ pb: space('xxl') }}>
        <Flex
          sx={{
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Typography sx={{ fontWeight: fontWeight('semibold') }}>
            Password
          </Typography>
          {editPassword ? (
            <Flex sx={{ gap: 1 }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setEditPassword(false)}
              >
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                color="primary"
                type="submit"
                loading={submitIsLoading}
                disabled={
                  !watch('currentPassword') ||
                  !watch('newPassword') ||
                  !watch('confirmPassword') ||
                  watch('newPassword') !== watch('confirmPassword')
                }
              >
                Save new password
              </LoadingButton>
            </Flex>
          ) : (
            <FlexColumn>
              <Button variant="outlined" onClick={onResetPasswordClick}>
                Reset password
              </Button>
            </FlexColumn>
          )}
        </Flex>
        <FieldsContainer>
          <FlexColumn>
            <InputLabel shrink>Current Password</InputLabel>
            {!editPassword ? (
              <TextField
                variant="outlined"
                size="large"
                disabled
                value="•••••••••••••••••"
              />
            ) : (
              <PasswordField
                size="large"
                {...register('currentPassword', {
                  required: 'This field is required',
                })}
              />
            )}
          </FlexColumn>
          {editPassword && (
            <Flex sx={{ gap: 2 }}>
              <FlexColumn sx={{ width: '100%' }}>
                <InputLabel shrink sx={{ color: 'content.primary' }}>
                  New password
                </InputLabel>
                <PasswordField
                  size="large"
                  {...register('newPassword', {
                    required: 'This field is required',
                  })}
                />
              </FlexColumn>
              <FlexColumn sx={{ width: '100%' }}>
                <InputLabel shrink sx={{ color: 'content.primary' }}>
                  Confirm new password
                </InputLabel>
                <PasswordField
                  size="large"
                  {...register('confirmPassword', {
                    required: 'This field is required',
                  })}
                />
              </FlexColumn>
            </Flex>
          )}
        </FieldsContainer>
        <ErrorMessage $show={!!error}>
          {(error as any)?.response?.data?.message}
        </ErrorMessage>
      </SettingsSection>
    </Form>
  );
};

export default UserProfile;
